"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const SequenceNumberHandler_1 = __importDefault(require("./SequenceNumberHandler"));
const TrackingIdEnum_1 = __importDefault(require("../enums/TrackingIdEnum"));
const IdGenerator_1 = __importDefault(require("./IdGenerator"));
const MetadataManager_1 = __importDefault(require("./MetadataManager"));
const localStorageKeys = [TrackingIdEnum_1.default.longTermIdKey, TrackingIdEnum_1.default.ipexIdKey];
const sessionStorageKeys = [TrackingIdEnum_1.default.sessionIdKey, TrackingIdEnum_1.default.planningIdKey];
/**
 * Utility class for handling data about the session.
 */
class SessionManager {
    constructor() {
        this.sequenceNumber = new SequenceNumberHandler_1.default();
        this.idGenerator = new IdGenerator_1.default();
    }
    /**
     * Get the current session Id if one exists, or creates one and returns it if it doesn't.
     *
     * @param key The request session type.
     * @returns The valid tracking Id.
     */
    getValidId(key) {
        const trackingId = this.getId(key);
        if (trackingId) {
            return trackingId;
        }
        return this.createId(key);
    }
    /**
     * Update the time for when the session started to now, and saves it to storage.
     * Will throw an Error if no session exists.
     *
     * @param key The key for the id to update.
     * @returns The new session id object which has been saved.
     */
    updateIdTime(key) {
        const pastId = this.getId(key);
        if (!pastId) {
            throw Error('No session to update');
        }
        const newIdProps = {
            time: Date.now(),
            id: pastId.id
        };
        if (localStorageKeys.includes(key)) {
            localStorage.setItem(key, JSON.stringify(newIdProps));
        }
        else if (sessionStorageKeys.includes(key)) {
            this.safeSessionSet(key, newIdProps);
        }
        return newIdProps;
    }
    /**
     * Ensure the ids in local and session storage exist and are valid. Will create and/or update values if necessary.
     */
    updateIds() {
        const sessionId = this.getId(TrackingIdEnum_1.default.sessionIdKey);
        let longTermId = this.getId(TrackingIdEnum_1.default.longTermIdKey);
        let ipexId = this.getId(TrackingIdEnum_1.default.ipexIdKey);
        if (!longTermId) {
            longTermId = this.createId(TrackingIdEnum_1.default.longTermIdKey);
        }
        if (!ipexId || Date.now() - ipexId.time > 30 * 60 * 1000) {
            ipexId = this.createId(TrackingIdEnum_1.default.ipexIdKey);
        }
        else {
            this.updateIdTime(TrackingIdEnum_1.default.ipexIdKey);
        }
        if (!sessionId) {
            this.createId(TrackingIdEnum_1.default.sessionIdKey);
            this.sequenceNumber.resetSequenceNumber();
            return {
                ['long_term_id_created']: longTermId.time,
                ['ipex_id_created']: ipexId.time
            };
        }
        else if (Date.now() - sessionId.time > 30 * 60 * 1000) {
            // No interaction the past 30 minutes. Create a new session, but leave the long term id as is.
            this.createId(TrackingIdEnum_1.default.sessionIdKey);
            this.sequenceNumber.resetSequenceNumber();
            return {
                ['long_term_id_created']: longTermId.time,
                ['ipex_id_created']: ipexId.time
            };
        }
        else {
            this.updateIdTime(TrackingIdEnum_1.default.sessionIdKey);
            return undefined;
        }
    }
    /**
     * Clear all session related data.
     */
    clearSessions() {
        localStorage.clear();
        sessionStorage.clear();
    }
    /**
     * Create a new id for given key, generates an uuidV4 as id and sets time to now.
     *
     * @param key The key to generate a value for.
     * @param startSource Optional planning start source, which will be saved if the key is for planning ids.
     * @param sourceDesignId Optional design id, which will be saved if the key is for planning ids.
     * @param sourceSpr Optional spr source, which will be saved if the key is for planning ids.
     * @returns The new value object.
     */
    createId(key, startSource, sourceDesignId, sourceSpr) {
        const id = this.idGenerator.uuidV4();
        const newIdProps = {
            time: Date.now(),
            id
        };
        if (key === TrackingIdEnum_1.default.planningIdKey && startSource) {
            newIdProps.startSource = startSource;
            newIdProps.sourceDesignId = sourceDesignId;
            newIdProps.sourceSpr = sourceSpr;
        }
        if (localStorageKeys.includes(key)) {
            localStorage.setItem(key, JSON.stringify(newIdProps));
        }
        else if (sessionStorageKeys.includes(key)) {
            this.safeSessionSet(key, newIdProps);
        }
        return newIdProps;
    }
    /**
     * Get the value for supplied key.
     *
     * @param key The key which to look for.
     * @returns The value for the key. Will be null if no value exists.
     */
    getId(key) {
        if (localStorageKeys.includes(key)) {
            const idProps = localStorage.getItem(key);
            return idProps ? JSON.parse(idProps) : null;
        }
        else if (sessionStorageKeys.includes(key)) {
            return this.safeSessionGet(key);
        }
        return null;
    }
    /**
     * Get user ids.
     *
     * @returns The user ids (sessionId, planningId, longTermId and ipexId).
     */
    getIds() {
        var _a, _b, _c, _d;
        return {
            sessionId: (_a = this.getId(TrackingIdEnum_1.default.sessionIdKey)) === null || _a === void 0 ? void 0 : _a.id,
            planningId: (_b = this.getId(TrackingIdEnum_1.default.planningIdKey)) === null || _b === void 0 ? void 0 : _b.id,
            longTermId: (_c = this.getId(TrackingIdEnum_1.default.longTermIdKey)) === null || _c === void 0 ? void 0 : _c.id,
            ipexId: (_d = this.getId(TrackingIdEnum_1.default.ipexIdKey)) === null || _d === void 0 ? void 0 : _d.id
        };
    }
    /**
     * Save some props to a planner-prepended key, but only if planner name is registered in metadata.
     *
     * @param key The session storage key to save to.
     * @param props The props to save to the key.
     */
    safeSessionSet(key, props) {
        const metadata = MetadataManager_1.default.getApplicationMetadata();
        if ('applicationId' in metadata) {
            const applicationIdKey = metadata.applicationId.concat('_', key);
            sessionStorage.setItem(applicationIdKey, JSON.stringify(props));
        }
    }
    /**
     * Retrieve some props from a planner-prepended key, but only if planner name is registered in metadata.
     *
     * @param key The session storage key to save to.
     * @returns The ITrackingProps saved to the key if it exists, otherwise null.
     */
    safeSessionGet(key) {
        const metadata = MetadataManager_1.default.getApplicationMetadata();
        let idProps;
        if (metadata === null || metadata === void 0 ? void 0 : metadata.applicationId) {
            const applicationIdKey = metadata.applicationId.concat('_', key);
            idProps = sessionStorage.getItem(applicationIdKey);
        }
        return idProps ? JSON.parse(idProps) : null;
    }
}
exports.default = SessionManager;
//# sourceMappingURL=SessionManager.js.map